import React from 'react'

import { EChartsOption, PieSeriesOption } from 'echarts'
import { PieChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import ReactECharts from 'echarts-for-react'

echarts.use([TitleComponent, TooltipComponent, LegendComponent, PieChart, CanvasRenderer])

export interface PieSliceData {
  name: string
  value: number
  color?: string
  itemStyle?: {
    color?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface TooltipFormatterParams {
  data: PieSliceData
  name: string
  value: number
  percent: number
}

interface EChartsTooltipParams {
  data: PieSliceData
  name: string
  value: number
  percent: number
  seriesName?: string
  dataIndex?: number
  color?: string
}

export interface DonutChartProps {
  title: string
  subTitle?: string
  data: PieSliceData[]
  seriesName?: string
  className?: string
  showLegend?: boolean | 'auto'
  tooltipFormatter?: (params: TooltipFormatterParams) => string
}

const defaultTooltipFormatter = (params: TooltipFormatterParams): string => {
  return `
    <div>
      <strong>${params.data.name}</strong><br/>
      Value: ${params.data.value}<br/>
    </div>`
}

export default function DonutChart({
  title,
  subTitle,
  showLegend,
  data,
  seriesName,
  tooltipFormatter = defaultTooltipFormatter,
}: DonutChartProps) {
  const pieSeries: PieSeriesOption = {
    name: seriesName || '',
    type: 'pie' as const,
    radius: ['40%', '70%'],
    avoidLabelOverlap: false,

    itemStyle: {
      borderRadius: 10,
      borderColor: '#fff',
      borderWidth: 2,
    },
    label: {
      show: false,
      position: 'center',
    },
    emphasis: {
      label: {
        show: true,
        fontSize: 20,
        fontWeight: 'bold',
      },
    },
    labelLine: {
      show: false,
    },
    data: data.map((item) => ({ ...item, itemStyle: item.color ? { color: item.color } : undefined })),
  }

  const option: EChartsOption = {
    title: {
      text: title,
      subtext: subTitle,
      left: 'center',
      textStyle: {
        fontSize: 18,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const typedParams = params as EChartsTooltipParams
        return tooltipFormatter({
          data: typedParams.data,
          name: typedParams.name,
          value: typedParams.value,
          percent: typedParams.percent,
        })
      },
    },
    legend: {
      show: showLegend && data && data.length > 0,
      orient: 'horizontal',
      left: 'center',
      bottom: '0',
      top: '90%',
    },
    series: [pieSeries],
  }

  return <ReactECharts option={option} style={{ height: '450px', width: '100%' }} />
}
